/**
 * TokenProvider glue — satisfies the `@linkhr/api-client` TokenProvider interface
 * so the api-client's `<ApiProvider tokenProvider={...}>` can consume the auth
 * package without importing it directly (D-12 seam).
 *
 * Shape mapping:
 *   - `getAccessToken()` — synchronous read from `accessTokenStore`.
 *   - `tryRefresh()` — `tryRefreshCrossTab` (cross-tab Locks + BC coordination).
 *     The api-client's `createSingleFlightRefresh` wraps THIS function for
 *     in-tab dedup; we add cross-tab dedup on top. Two layers, no re-entry.
 *   - `onAuthFailure(cause)` — pass-through to a caller-supplied handler. Plan 05's
 *     AuthProvider supplies the concrete implementation (clears AuthStore,
 *     navigates to /login with deep-link preserved).
 */
import type { TokenProvider } from "@linkhr/api-client";
import type { ApiError } from "@linkhr/api-client";
import { accessTokenStore } from "./accessTokenStore.js";
import { tryRefreshCrossTab, getAudienceHeader } from "../refresh/crossTabCoordinator.js";

export interface TokenProviderOpts {
  /**
   * Called when the api-client middleware gives up (refresh itself failed,
   * family revoked, etc.). Plan 05's AuthProvider wires this to clear AuthStore
   * and route.navigate to /login.
   */
  onAuthFailure: (cause: ApiError) => void;
  /** An API call answered 403 AUTH_PASSWORD_CHANGE_REQUIRED (see TokenProvider). */
  onPasswordChangeRequired?: () => void;
}

export function createTokenProvider(opts: TokenProviderOpts): TokenProvider {
  return {
    getAccessToken: () => accessTokenStore.get(),
    tryRefresh: () => tryRefreshCrossTab(),
    onAuthFailure: opts.onAuthFailure,
    ...(opts.onPasswordChangeRequired !== undefined
      ? { onPasswordChangeRequired: opts.onPasswordChangeRequired }
      : {}),
    getAudienceHeader: () => getAudienceHeader(),
  };
}
